import { Flex, Text } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { GroupBase, Props, CreatableSelect } from "chakra-react-select";
import {
  BorrowerCompanyForTheCompanyModel,
  useBorrowerCompaniesForTheTenant,
} from "hooks/useBorrowerCompaniesForTheTenant";
import { AlertsContext } from "providers/alerts";
import { useContext, useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";

interface NewCompanyOption {
  label: string;
  value: string;
  __isNew__: boolean;
}

interface CustomOptionProps {
  data: BorrowerCompanyForTheCompanyModel | NewCompanyOption;
  innerProps: any;
}

const CustomOption = ({ data, innerProps }: CustomOptionProps) => {
  const newCompanyOption = data as NewCompanyOption;
  const company = data as BorrowerCompanyForTheCompanyModel;

  return (
    <Flex
      {...innerProps}
      p={2}
      gap={4}
      borderRadius="md"
      _hover={{ bg: "gray.50" }}
      cursor="pointer"
      alignItems="center"
    >
      <Flex direction="column" alignItems="start">
        {newCompanyOption.__isNew__ ? (
          <Text color="gray.600">{newCompanyOption.label}</Text>
        ) : (
          <>
            <Text color="gray.600">{company.name}</Text>
            <Text fontSize="sm" color="gray.500">
              {company.address}
            </Text>
          </>
        )}
      </Flex>
    </Flex>
  );
};

interface BorrowerCompanySelectProps
  extends Props<
    BorrowerCompanyForTheCompanyModel,
    false,
    GroupBase<BorrowerCompanyForTheCompanyModel>
  > {
  borrowerCompanyId: string | undefined;
  setBorrowerCompanyId: (borrowerCompany: string | undefined) => void;
}

export const BorrowerCompanySelect = ({
  borrowerCompanyId,
  setBorrowerCompanyId,
  ...props
}: BorrowerCompanySelectProps) => {
  const { showError } = useContext(AlertsContext);
  const { data: companies, isLoading, refetch } = useBorrowerCompaniesForTheTenant();
  const [options, setOptions] = useState<BorrowerCompanyForTheCompanyModel[]>([]);
  const [selectedBorrowerCompany, setSelectedBorrowerCompany] =
    useState<BorrowerCompanyForTheCompanyModel | null>(null);

  useEffect(() => {
    if (companies) {
      setOptions(companies.sort((a, b) => a.name.localeCompare(b.name)));
    }
  }, [companies]);

  useEffect(() => {
    if (borrowerCompanyId && companies) {
      const newSelectedCompany = companies.find(o => o.id === borrowerCompanyId) ?? null;
      if (newSelectedCompany?.id !== selectedBorrowerCompany?.id) {
        setSelectedBorrowerCompany(newSelectedCompany);
      }
    }
  }, [borrowerCompanyId, companies, selectedBorrowerCompany]);

  const { mutate: createCompany, isPending: isInsertingCompany } = useMutation({
    mutationFn: async (name: string) => {
      const { company } = await apiRequest("/supabase/companies/insert", {
        name,
      });
      return company;
    },
    onSuccess: company => {
      refetch();
      handleSelectChange(company);
    },
    onError: err => {
      showError({
        title: "Something Went Wrong",
        description: getErrorMessage(err),
      });
      console.error(err);
    },
  });

  const handleSelectChange = (borrowerCompany: BorrowerCompanyForTheCompanyModel | null) => {
    if (borrowerCompany?.id !== borrowerCompanyId) {
      setSelectedBorrowerCompany(borrowerCompany);
      setBorrowerCompanyId(borrowerCompany?.id ?? undefined);
    }
  };

  return (
    <CreatableSelect<BorrowerCompanyForTheCompanyModel>
      options={options}
      getOptionLabel={b => b.name || ""}
      getOptionValue={b => b.id || ""}
      components={{ Option: CustomOption }}
      isLoading={isLoading || isInsertingCompany}
      chakraStyles={{
        placeholder: provided => ({
          ...provided,
          textAlign: "left",
        }),
        singleValue: provided => ({
          ...provided,
          textAlign: "left",
        }),
      }}
      onCreateOption={createCompany}
      placeholder="Select a company..."
      onChange={handleSelectChange}
      value={selectedBorrowerCompany}
      variant="primary"
      isClearable={true}
      {...props}
    />
  );
};
